<template>
  <v-container fluid class="pb-0 pt-0 h-100">
    <v-row class="h-100">
      <v-col cols="12" class="pa-0 h-100">
        <v-card :elevation="embedded ? 0 : 2" class="h-100 d-flex flex-column" elevation="0" outlined>
          <div class="d-flex align-center justify-content-between pl-3">
            <div class="d-flex">
                <img v-if="report.playerId"
                    class="rounded-lg"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/' + report.playerId + '.png'"
                    :height="'45px'"
                    contain
                    @error="onImageLoadFailure($event,'player')"
                />
                <v-icon large color="primary" v-if="!report.playerId">{{getIconByPetition(report.petition)}}</v-icon>
                <v-icon color="secondary" class="ml-n4 mt-9" >assignment</v-icon>
            </div>
            <div class="flex-grow-1">
              <v-card-title>
                <div class="d-flex align-center justify-space-between w-100">
                  <router-link :to="`/report/${report.reportId}`" class="nav-link">
                    <div class="d-flex" v-bind:style="{'max-width': embedded || $vuetify.breakpoint.mdAndDown ? '155px' : 'auto'}">
                      <span class="text-ellipsis">
                        {{report.petition === 'Deep Report' ? report.playerName : $t(`requestReport.categories.${report.petition.toLowerCase()}.title`)}}
                      </span>
                      <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" v-if="report.isSupervised" color="secondary" class="ml-3">visibility</v-icon>
                        </template>
                        <span class="black--text body-1">{{ $t('collections.privacity.supervisedDescription') }} </span>
                      </v-tooltip>
                      <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" v-if="report.isPublic" color="secondary" class="ml-3">public</v-icon>
                        </template>
                        <span class="black--text body-1">{{ $t('collections.privacity.publicDescription') }} </span>
                      </v-tooltip>
                    </div>
                  </router-link>
                  <v-btn outlined color="secondary" x-small :to="`/player/${report.playerId}`" v-if="report.petition === 'Deep Report'"><v-icon small>remove_red_eye</v-icon></v-btn>
                </div>

              </v-card-title>
              <v-card-subtitle>
                <div class="d-flex" v-if="!userHasRole('ROLE_ADMIN')">
                  <span>{{$t(report.stateCode)}}</span>
                  <v-icon small class="ml-1" :color="getCircleColorByState(report.stateId)">album</v-icon>
                </div>
                <div class="state-selector" v-if="userHasRole('ROLE_ADMIN')">
                  <v-select
                    v-model="report.stateId"
                    :items="states"
                    hide-details
                    flat
                    outlined
                    solo
                    dense
                    item-value="id"
                    item-text="descEng"
                    height="25px"
                    @change="onStateChange($event)"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="body-2">{{$t(item.desEng)}}</span>
                      <v-icon small class="ml-1" :color="getCircleColorByState(item.id)">album</v-icon>
                    </template>
                    <template slot="item" slot-scope="data">
                      <span class="body-2">{{$t(data.item.desEng)}}</span>
                      <v-icon small class="ml-1" :color="getCircleColorByState(data.item.id)">album</v-icon>
                    </template>
                  </v-select>
                </div>
              </v-card-subtitle>
            </div>
            <div class="" @click="onNavigateReportClick()">
              <router-link class="pr-3 cursor-pointer" :to="`/report/${report.reportId}`">
                <v-badge
                  color="red"
                  :content="report.newMessages.length"
                  overlap
                  v-if="report.newMessages && report.newMessages.length"
                >
                  <v-icon color="secondary">message</v-icon>
                </v-badge>
                <v-icon color="secondary" v-if="!(report.newMessages && report.newMessages.length)">message</v-icon>
              </router-link>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="primary--text flex-grow-1">
            <div class="d-flex justify-space-between">
              <span class="subheader-font">{{$t("reports.requestAt")}}</span>
              <span class="monospace-font">{{report.createdAt}}</span>
            </div>
            <div class="d-flex justify-space-between body-2 mt-n1 pt-1" v-if="showCreator">
              <span class="subheader-font">{{$t("general.by")}}</span>
              <span class="font-weight-bold">{{report.userName}}</span>
            </div>
            <div class="d-flex justify-space-between body-2 mt-n1" v-if="userHasRole('ROLE_ADMIN')">
              <span></span>
              <span class="subheader-font">{{report.clientName}}</span>
            </div>
            <div class="d-flex flex-column justify-space-between mt-2" v-if="report.petition === 'Deep Report'">
              <span class="subheader-font">{{$t("reports.features")}}</span>
              <ul class="font-weight-medium primary--text body-2">
                <li v-if="report.isPerformance">{{$t("reports.performance")}}</li>
                <li v-if="report.isEvolution">{{$t("reports.evolution")}}</li>
                <li v-if="report.isCompareMyTeam">{{$t("reports.compareWithMyTeam")}}</li>
                <li v-if="report.isSimilarPlayers">{{$t("reports.similarPlayers")}}</li>
              </ul>
            </div>
            <div class="d-flex flex-column justify-space-between mt-2" v-if="report.notes">
              <span class="subheader-font">{{$t("general.notes")}}</span>
              <ul>
                <li style="list-style-type: none;">{{report.notes}}</li>
              </ul>
            </div>
            <div class="d-flex flex-column justify-space-between mt-2">
              <span class="subheader-font">{{$t("settings.language")}}</span>
              <ul>
                  <div class="d-flex align-center">
                    <DriblabCountryFlag size="small" :iso="report.langName === 'EN' ? 'gb' : report.langName.toLowerCase() "></DriblabCountryFlag>
                    <small class="primary--text mx-2">{{ report.langName }}</small>
                  </div>
              </ul>
            </div>
            <div
              class="d-flex justify-space-between body-2 mt-2"
              v-if="report.stateId === 3 && report.filename"
            >
              <span class="subheader-font">{{$t("general.updated")}}</span>
              <span class="monospace-font">{{report.updatedAt}}</span>
            </div>
          </v-card-text>
          <v-divider v-if="!embedded"></v-divider>
          <v-card-actions v-if="report.stateId === 3 && report.filename && !userHasRole('ROLE_ADMIN')">
            <v-btn color="primary" block small @click="onDownloadClick()" :loading="loadingFile" rounded class="elevation-0 monospace-font">
              <v-icon small class="mr-2">file_download</v-icon>
              {{$t("general.download")}}
            </v-btn>
          </v-card-actions>
          <v-card-actions class="d-flex align-end" v-if="userHasRole('ROLE_ADMIN')" style="min-height:64px;">
            <div class="file-detail" v-if="report.filename">
              <v-icon @click="onDownloadClick()" color="secondary">file_download</v-icon>
              <span class="mx-2 body-1" @click="onDownloadClick()">{{report.filename}}</span>
              <v-icon small @click="onDeleteFileClick(report)" color="error">delete_forever</v-icon>
            </div>
            <div class="file-selector" v-if="!report.filename">
              <v-file-input
                hide-details
                prepend-icon="attach_file"
                flat
                solo
                dense
                height="25px"
                :loading="report.loadingFile"
                :disabled="report.loadingFile"
                :placeholder="$t('general.uploadFile')"
                @change="onFileChange(report,$event)"
                class="d-flex align-center"
              ></v-file-input>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ReportService from "../../services/report.service";
import {userHasRole} from '../../utils/functions';
import DriblabCountryFlag from '../base/DriblabCountryFlag'

export default {
  name: "InboxReportItem",
  components: {
    DriblabCountryFlag
  },
  data() {
    return {
        loadingFile: false
    };
  },
  props: {
    report: {
      type: Object,
      required: true,
      default: {}
    },
    embedded: {
      type: Boolean,
      default: false
    },
    states: {
      type: Array,
      default: []
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentRoute() {
      return this.$route.name;
    },
    showCreator(){
      return this.userHasRole('ROLE_ADMIN') || this.report.userId != this.currentUser.id;
    }
  },
  watch:{
    "report": async function(newVal, oldVal) {
      this.report.stateIdBackup = this.report.stateId;
    },
    "$store.state.reports.hasToUpdateNewMessages": function (newVal, oldVal) {
      if(this.$store.state.reports.lastPayload
      && this.$store.state.reports.lastPayload.reportId === this.report.reportId
      && this.currentRoute != 'report-detail'){
        this.loadNewMessages()
      }
    }
  },
  created() {},
  mounted() {
    this.report.stateIdBackup = this.report.stateId;
    this.loadNewMessages();
  },
  methods: {
    getCircleColorByState(stateId) {
      switch (stateId) {
        case 0:
          return "primary";
        case 1:
          return "error darken-1";
        case 2:
          return "warning lighten-1";
        case 3:
          return "success darken-1";
      }
    },
    onImageLoadFailure(event, type) {
      if (type === "league" || type === "team") {
        event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
      } else if (type === "player") {
        event.target.src =
          "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/0.png";
      }
    },
    async onDownloadClick(){
        if(!this.loadingFile){
            this.loadingFile = true;
            let filename = this.report.filename;
            let urlResponse = await ReportService.getDownloadURL(this.report.reportId,filename);
            let url = urlResponse.data;
            window.open(url);
            if(!this.currentUser.impostor && this.report.userId === this.currentUser.id && !this.report.readed){
                await ReportService.read(this.report.reportId);
                this.report.isReaded = true;
                this.$store.dispatch('notificationsChange', true);
            }
            this.loadingFile = false;
        }
    },
    userHasRole(role) {
      return userHasRole(this.currentUser,role);
    },
    async onStateChange(stateId) {
      let oldState = _.find(this.states, s => s.id === this.report.stateIdBackup);
      let newState = _.find(this.states, s => s.id === stateId);

      await ReportService.updateReportState(this.report.reportId, newState);

      await ReportService.postReportMessage(this.report.reportId,{
        reportId:this.report.reportId,
        userId: 0,
        message: `State Updated: ${oldState.nameEng} to ${newState.nameEng}`
      })

      this.$emit("stateChange",true);
    },
    async onFileChange(report, file) {
      if (file && !report.loadingFile) {
        report.loadingFile = true;
        this.$forceUpdate();
        let urlResponse = await ReportService.getUploadURL(
          report.reportId,
          file.name
        );
        let url = urlResponse.data;
        await ReportService.upload(url, file);
        await ReportService.updateReportFilename(
          report.reportId,
          `${file.name}`
        );
        report.filename = file.name;

        await ReportService.postReportMessage(this.report.reportId,{
          reportId:this.report.reportId,
          userId: 0,
          message: `File Uploaded`
        })
        this.$emit("fileUploaded",true);

        report.loadingFile = false;
        this.$forceUpdate();
      }
    },
    async onDeleteFileClick(report) {
      if (!report.loadingFile) {
        report.loadingFile = true;
        let filename = report.filename;
        report.filename = null;
        let urlResponse = await ReportService.getDeleteURL(
          report.reportId,
          filename
        );
        let url = urlResponse.data;
        await ReportService.delete(url);
        await ReportService.updateReportFilename(report.reportId, null);

        await ReportService.postReportMessage(this.report.reportId,{
          reportId:this.report.reportId,
          userId: 0,
          message: `File Deleted`
        })
        this.$emit("fileDeleted",true);

        report.loadingFile = false;
        this.$forceUpdate();
      }
    },
    async loadNewMessages(){
      let reportResponse = await ReportService.getNewMessages(this.report.reportId);
      this.report.newMessages = _.filter(reportResponse.data,(d) => this.currentUser.clientId != 30 || d.userId != 0 || d.message === 'Report Requested');
      this.$forceUpdate();
    },
    getIconByPetition(petition){
      switch(petition){
        case "Scouting":
          return "center_focus_strong"
          break;
        case "Coaches":
          return "hail"
          break;
        case "Team Analysis":
          return "security"
          break;
        case "Game Analysis":
          return "pivot_table_chart"
          break;
        case "Others":
          return "emoji_objects"
          break;
        case "Conversation":
          return "supervised_user_circle"
          break;
        default:
          return "cases"
          break;
      }
    },
    onNavigateReportClick(){
      if(this.report.newMessages && this.report.newMessages.length){
        setTimeout(() => {
          this.loadNewMessages();
          this.$store.dispatch('updateNewMessages');
        },2500)
      }
    }
  }
};
</script>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/views/inbox/InboxReportItem.vue